import axios from '@api';
import { normalizeTopicIds } from '@helpers';

/**
 * create pinned answer
 * @param  {string} documentId
 * @param  {Object} option
 * @param  {string} promptId
 * @return {Promise}
 */
export const createPinnedAnswer = ({ documentId, option, promptId }) => {
  return axios({
    data: {
      document_id: documentId,
      ...option,
      prompt_id: promptId,
    },
    method: 'post',
    url: '/v2/api/pinned-answers',
    withCredentials: true,
  });
};

/**
 * read prompt search results
 * @param  {Array} attributeIds
 * @param  {string} documentId
 * @param  {string} entryId
 * @param  {string} narrativeId
 * @param  {string} promptId
 * @param  {Object} searchAnswers
 * @param  {Array} searchPrompts
 * @param  {string} searchQuery
 * @param  {string} serviceName World Panel service-name context. Empty by
 *   default — callers scope the search by passing the service from the object's
 *   own response (e.g. the Builder forwards the loaded document's
 *   `service_name`); when the object carries none, the param is omitted.
 * @param  {string} signal AbortController signal
 * @param  {Array<string|number>} topicIds
 * @return {Promise}
 */
export const readSearchResults = ({
  attributeIds,
  documentId,
  entryId,
  narrativeId,
  promptId,
  searchAnswers = {},
  searchPrompts = [],
  searchQuery,
  serviceName = '',
  signal,
  topicIds = [],
}) => {
  const answers = searchPrompts?.flat().includes(promptId) ? JSON.stringify(searchAnswers) : {};
  const normalizedTopicIds = normalizeTopicIds(topicIds);
  const isDynamicNarrative = narrativeId === 'dynamic';
  return axios({
    method: 'get',
    params: {
      answers,
      attributeId: attributeIds.join(','),
      documentId,
      narrativeId,
      parent_id: entryId,
      search: searchQuery,
      serviceName: serviceName || undefined,
      ...(isDynamicNarrative && normalizedTopicIds.length > 0
        ? { topicId: normalizedTopicIds.join(',') }
        : {}),
    },
    signal,
    url: `/api/prompts/${promptId}/search`,
    withCredentials: true,
  });
};

/**
 * read pinned answers
 * @param  {string} documentId
 * @param  {string} signal
 * @return {Promise}
 */
export const readPinnedAnswers = ({ documentId, signal }) => {
  return axios({
    method: 'get',
    params: {
      document_id: documentId,
    },
    signal,
    url: '/v2/api/pinned-answers/',
    withCredentials: true,
  });
};

/**
 * delete pinned answer
 * @param  {string} pinnedAnswerId
 * @return {Promise}
 */
export const deletePinnedAnswer = ({ pinnedAnswerId }) => {
  return axios({
    method: 'delete',
    url: `/v2/api/pinned-answers/${pinnedAnswerId}`,
    withCredentials: true,
  });
};
